import {
  getQuestionSetCategory,
  makeRollupWeightFn,
  rollupWeightedScores,
} from 'js/utils/question-set';
import { getAnswerDomain } from 'js/utils/scoring';
import {
  buildRecommendationsDictionary,
  lookupNameInDictionary,
} from 'js/utils/template';

function inferQuestionType(templateQuestion, scoredQuestion) {
  return (
    scoredQuestion?.question_type ??
    templateQuestion?.type ??
    templateQuestion?.elementType ??
    'likert'
  );
}

function resolveAssessment(assessmentTemplate) {
  if (!assessmentTemplate) {
    return null;
  }

  return assessmentTemplate.assessment ?? assessmentTemplate;
}

function ensureSet(sets, key) {
  if (!sets.has(key)) {
    sets.set(key, {
      key,
      name: undefined,
      category: undefined,
      ordinal: undefined,
      weight: undefined,
      questions: new Map(),
    });
  }
  return sets.get(key);
}

function upsertQuestion(questionMap, question) {
  if (!question?.key) {
    return;
  }

  const previous = questionMap.get(question.key) ?? {};
  const incomingAnswers = Array.isArray(question.answers)
    ? question.answers
    : null;

  questionMap.set(question.key, {
    key: question.key,
    text: question.text ?? question.name ?? previous.text,
    type: question.type ?? previous.type,
    answers:
      incomingAnswers && incomingAnswers.length > 0
        ? incomingAnswers
        : previous.answers,
    weight: question.weight ?? previous.weight,
    ordinal: question.ordinal ?? previous.ordinal,
  });
}

function addQuestionSetDef(sets, qs) {
  if (!qs?.key) {
    return;
  }

  const entry = ensureSet(sets, qs.key);
  if (qs.name) {
    entry.name = qs.name;
  }
  if (qs.category) {
    entry.category = qs.category;
  }
  if (qs.ordinal != null) {
    entry.ordinal = qs.ordinal;
  }
  if (qs.weight != null) {
    entry.weight = qs.weight;
  }
  for (const question of qs.questions ?? []) {
    upsertQuestion(entry.questions, question);
  }
}

export function collectQuestionSetDefs(template, catalog, scored = {}) {
  const sets = new Map();

  for (const qs of template?.question_sets ?? []) {
    addQuestionSetDef(sets, qs);
  }
  for (const qs of catalog?.question_sets ?? []) {
    addQuestionSetDef(sets, qs);
  }

  for (const [qsKey, qsScored] of Object.entries(scored ?? {})) {
    const entry = ensureSet(sets, qsKey);
    for (const [qKey, qScored] of Object.entries(qsScored?.questions ?? {})) {
      const previous = entry.questions.get(qKey);
      if (!previous) {
        upsertQuestion(entry.questions, {
          key: qKey,
          text: qKey,
          type: qScored?.question_type,
        });
      } else if (!previous.type && qScored?.question_type) {
        previous.type = qScored.question_type;
      }
    }
  }

  return [...sets.values()].map((qs) => ({
    key: qs.key,
    name: qs.name,
    category: qs.category,
    ordinal: qs.ordinal,
    weight: qs.weight,
    questions: [...qs.questions.values()],
  }));
}

function buildQuestionEntry(question, qScored, name) {
  const qKey = question.key;
  const normalizedScore = qScored?.score ?? null;
  const answerDomain = getAnswerDomain(
    question.answers ?? [],
    'value',
    2,
    [0, 1]
  );
  const [min, max] = answerDomain;
  const meanScore =
    normalizedScore == null ? null : min + normalizedScore * (max - min);

  return {
    key: qKey,
    name: name || question.text || question.name || qKey,
    type: inferQuestionType(question, qScored),
    answerDomain,
    meanScore,
    normalizedScore,
    responseCount: normalizedScore == null ? 0 : 1,
    weight: question.weight,
  };
}

/**
 * Builds a cumulative dataMap for org radial pie and radar charts.
 * Question sets and questions are the union of the latest template, the
 * instance catalog, and scored keys so older-revision items still plot.
 * Labels prefer catalog text, then score-row text, then the latest template.
 */
export function buildOrgHistoryDataMap(
  assessmentTemplate,
  scoredNode,
  catalog = null,
  questionScores = null
) {
  const template = resolveAssessment(assessmentTemplate);
  const scored = scoredNode?.scores ?? {};
  const questionSets = collectQuestionSetDefs(template, catalog, scored);
  const names = buildRecommendationsDictionary(
    assessmentTemplate,
    catalog,
    questionScores
  );

  const dataMap = {
    categories: {},
    questionSets: {},
  };

  for (const qs of questionSets) {
    const qsKey = qs.key;
    const qsScored = scored[qsKey];
    const questions = {};

    for (const q of qs.questions ?? []) {
      questions[q.key] = buildQuestionEntry(
        q,
        qsScored?.questions?.[q.key],
        lookupNameInDictionary(names, qsKey, q.key)
      );
    }

    const questionList = Object.values(questions);
    const normalizedQsScore = rollupWeightedScores(questionList, {
      getScore: (question) => question.normalizedScore,
      getWeight: makeRollupWeightFn(questionList),
    });

    const category = getQuestionSetCategory(qs);

    dataMap.questionSets[qsKey] = {
      key: qsKey,
      displayName: lookupNameInDictionary(names, qsKey) || qs.name || qsKey,
      category,
      questions,
      normalizedScore: normalizedQsScore,
      weight: qs.weight,
    };

    dataMap.categories[category] = {
      ...dataMap.categories[category],
      [qsKey]: null,
    };
  }

  return dataMap;
}
